import {Tag, TagColor, TagSize} from '@benchling/nucleus-ui';
import {StyleSheet, css} from 'aphrodite';
import classNames from 'classnames';
import React, {useMemo} from 'react';

import Button, {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import {BLUE_LIGHT} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {OverflowButton} from 'ui-library/menus/OverflowMenu';

import {
  isButtonActionCustomButton,
  isDataViewActionFunctionType,
  useDataViewContext,
} from './DataViewContext';
import getViewMetadataModalOverflowAction from './metadata-modal/getViewMetadataModalOverflowAction';

export default function DataViewToolbar(): JSX.Element {
  return (
    <div className={css(dataViewToolbarStyles.toolbar)}>
      <DataViewWarningBadge />
      <DataViewActionButtons />
      <DataViewOverflowButton />
    </div>
  );
}

/** @package
 *
 * Define our own warnings instead of using Pane's built-in warnings because we want to show a badge with
 * text, rather than just an icon.
 * */
export function DataViewWarningBadge(): JSX.Element | null {
  const {warning} = useDataViewContext();

  if (warning == null) {
    return null;
  }

  const {label, hoverMessage} = warning;

  return (
    <Tooltip text={hoverMessage}>
      <Tag color={TagColor.WARN} icon="exclamation-triangle" text={label} />
    </Tooltip>
  );
}

/** @package */
export function DataViewActionButtons(): JSX.Element {
  const {
    actions: {buttonActions},
    actionInput,
  } = useDataViewContext();

  return (
    <>
      {buttonActions.map((buttonAction, index) => {
        if (isButtonActionCustomButton(buttonAction)) {
          if (buttonAction != null) {
            return <div key={index}>{buttonAction}</div>;
          } else {
            return null;
          }
        }

        let realizedButtonAction;
        if (isDataViewActionFunctionType(buttonAction)) {
          realizedButtonAction = buttonAction(actionInput);
        } else {
          realizedButtonAction = buttonAction;
        }
        if (realizedButtonAction == null) {
          return null;
        }

        const {icon, isAlternativeButtonColor, isDisabled, isToggled, helpText, label, onClick} =
          realizedButtonAction;
        return (
          <Tooltip text={helpText} key={index}>
            <Button
              aria-label={label == null ? helpText : undefined}
              buttonStyle={isAlternativeButtonColor ? ButtonStyle.TERTIARY_BLUE : ButtonStyle.TERTIARY_GRAY}
              disabled={isDisabled}
              className={classNames(css(dataViewToolbarStyles.button), isToggled && 'is-active')}
              icon={icon}
              onClick={isDisabled ? undefined : onClick}
              size={ButtonSize.MINI}
              text={label}
            />
          </Tooltip>
        );
      })}
    </>
  );
}

/** @package */
export function DataViewOverflowButton(): JSX.Element | null {
  const {
    actions: {overflowActions},
    actionInput,
    metadataContent,
    title,
  } = useDataViewContext();

  const realizedOverflowActions = useMemo(
    () => [
      ...overflowActions
        .map((overflowAction) => {
          if (typeof overflowAction === 'function') {
            return overflowAction(actionInput);
          }
          return overflowAction;
        })
        .filter((overflowAction) => overflowAction != null),
      getViewMetadataModalOverflowAction({
        title: `${title} metadata`,
        gridCells: [{component: title, label: 'Name'}, ...(metadataContent?.gridCells ?? [])],
        additionalContent: metadataContent?.additionalContent,
      }),
    ],
    [actionInput, metadataContent, overflowActions, title]
  );

  if (realizedOverflowActions.length === 0) {
    return null;
  }

  return (
    <Dropdown
      menu={
        <Menu dropLeft={true} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.LARGE}>
          {realizedOverflowActions.map(
            ({helpText, icon, isDisabled, isToggled, label, onClick, isBeta, submenu}, index) => (
              <Option
                key={index}
                onSelect={isDisabled ? undefined : onClick}
                tooltipTitle={helpText}
                icon={icon}
                iconClassName={css(isToggled && dataViewToolbarStyles.toggledIcon)}
                isDisabled={isDisabled}
              >
                <div className={css(dataViewToolbarStyles.optionLabel)}>
                  {label}
                  {isBeta && (
                    <span className={css(dataViewToolbarStyles.betaTag)}>
                      <Tag color={TagColor.BETA} size={TagSize.SMALL} text="BETA" />
                    </span>
                  )}
                </div>
                {submenu}
              </Option>
            )
          )}
        </Menu>
      }
    >
      <OverflowButton enableTooltip={true} className={css(dataViewToolbarStyles.button)} />
    </Dropdown>
  );
}

/** @package */
export const dataViewToolbarStyles = StyleSheet.create({
  button: {
    marginLeft: 4,
  },
  toggledIcon: {
    color: BLUE_LIGHT,
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'row',
  },
  optionLabel: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
  },
  betaTag: {
    flexShrink: 0,
    // Tag comes with a 8px margin, we need to offset it
    marginRight: -8,
  },
});
